import type { ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { cn } from '~/lib/cn';

import { FieldMessage } from '../FieldMessage';
import { Label } from '../Label';
import { assertNonEmptyName } from './assertions/nonEmptyName';

export interface FormFieldProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  label: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

const FormField = <TFieldValues extends FieldValues>({
  name,
  label,
  disabled,
  className,
  children,
}: FormFieldProps<TFieldValues>) => {
  assertNonEmptyName(name);

  const {
    fieldState: { error },
  } = useController<TFieldValues>({ name, disabled });

  return (
    <Label
      disabled={disabled}
      className={cn('flex', 'flex-col', 'gap-sm', className)}
    >
      {label}
      {children}
      {error && <FieldMessage state="invalid">{error.message}</FieldMessage>}
    </Label>
  );
};

FormField.displayName = 'FormField';
export { FormField };
